// @ts-nocheck
import useAccount from "classes/Accounts/hooks/useAccount";
import LoadCont from "components/LoadCont/LoadCont";
import genRequest from "includes/request";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AccumulationChart from "components/Charts/AccumulationChart/AccumulationChart";
import useTemplate from "hooks/useTemplate";
import { gen_color_palette } from "includes/Colors";
import { PieSeries } from "@syncfusion/ej2-react-charts";
import useTheme from "hooks/useTheme";
import Button from "components/Button/Button";
import { t } from "i18next";

export default function ConnectionOrigins(props) {
    const { account } = useAccount();
    const [viewMode, setViewMode] = useState("browser"); // "browser" ou "origin"

    const isMobile = (origin) => origin === 1;

    const countConnections = (rows) =>
        rows.reduce((sum, row) => sum + (Number(row.count) || 0), 0);

    const treat_data = (connections) => {
        if (!connections?.length) return [];

        // Filtrer les connexions qui ont un navigateur
        const filteredConnections = connections.filter(connection => connection.browser);

        if (!filteredConnections.length) return [];

        let ret = [];

        filteredConnections.forEach((connection) => {
            let identifier;

            if (viewMode === "browser") {
                // Grouper par navigateur
                const browserMatch = connection.browser.match(/(\w+)_/);
                identifier = browserMatch ? browserMatch[1] : connection.browser;
            } else {
                // Grouper par origine (Mobile/Desktop)
                identifier = isMobile(connection.origin) ? "Mobile" : "Desktop";
            }

            let found = ret.findIndex((item) => item.identifier === identifier);

            if (found === -1) {
                ret.push({
                    identifier: identifier,
                    connections: [connection],
                    count: 0,
                });
                found = ret.length - 1;
            } else {
                ret[found].connections.push(connection);
            }
            // The API returns one row per (browser, origin) with its `count`.
            ret[found].count += Number(connection.count) || 0;
        });

        // Calculer les pourcentages
        const total = countConnections(filteredConnections);
        ret.forEach((item) => {
            item.percentage = ((item.count / total) * 100).toFixed(2);
        });

        // Trier par nombre de connexions (décroissant)
        return ret.sort((a, b) => b.count - a.count);
    };

    const getData = () => {
        return genRequest("stats/browser-origins/" + account.companyId);
    };

    const { isLoading, data, refetch, isFetching } = useQuery({
        queryKey: ["browserOriginStats", account.companyId],
        queryFn: getData,
        enabled: !!account.companyId
    });

    useEffect(() => {
        if (account.companyId) {
            refetch();
        }
    }, [viewMode, account.companyId]);

    return (
        <div className="stats-cont">
            <div className="d-flex gap-2 justify-content-between">
                <div className="stat-module-title">
                    {t("Statistic_Page.Connection_Origins")}
                </div>
                {(!isLoading && !isFetching && data) && (
                    <div>{countConnections(data.filter(c => c.browser))} {t('Statistic_Page.Connections')}</div>
                )}
            </div>

            <div className="d-flex gap-4 mt-2 flex-wrap">
                <div>
                    {t("Statistic_Page.Sort")}:
                    <div className="buttons-group" style={{ height: "37px" }}>
                        <Button
                            className={
                                "w-auto h-100 " +
                                (viewMode === "origin" ? "btn-primary" : "btn-bg")
                            }
                            onClick={() => setViewMode("origin")}
                        >
                            {t("Statistic_Page.Platform")}
                        </Button>
                        <Button
                            className={
                                "w-auto h-100 " +
                                (viewMode === "browser" ? "btn-primary" : "btn-bg")
                            }
                            onClick={() => setViewMode("browser")}
                        >
                            {t("Statistic_Page.By_Browser")}
                        </Button>
                    </div>
                </div>
            </div>

            {isLoading || isFetching ? (
                <LoadCont />
            ) : (
                <Origins
                    key={`connection-origins-${viewMode}`}
                    viewMode={viewMode}
                    stats={treat_data(data || [])}
                />
            )}
        </div>
    );
}

function getChartSpecs(viewType) {
    let chartTypesServices = [{ services: [PieSeries], type: "Pie" }];
    let found = chartTypesServices.find((a) => a.type === viewType);
    return found ? found : {};
}

function Origins({ stats, viewMode }) {
    const { template } = useTemplate();
    const { getTheme } = useTheme();
    let palettes = gen_color_palette(template.primaryColor(), stats?.length);

    if (!stats?.length) {
        return (
            <div className="mt-4 w-100 text-center text-infos">
                {t("Commons.NO_CONNECTIONS")}
            </div>
        );
    }

    const genSeries = (stats) => {
        return [
            {
                dataSource: stats.map((row) => {
                    let label = `${row.percentage}% - ${row.identifier || "Non défini"}`;
                    return {
                        x: label,
                        y: Number(row.percentage),
                        count: row.count
                    };
                }),
                xName: "x",
                yName: "y",
                innerRadius: "60%",
                tooltip: {
                    enable: true,
                    format: "${point.x}<br/>Connexions: ${point.count}"
                },
                type: "Pie",
                name: viewMode === "browser" ? "Navigateurs" : "Origines des connexions",
                palettes,
                legendShape: "Circle",
            },
        ];
    };

    const genTile = (stats) => {
        let series = genSeries(stats);

        let chart = {
            id: "Analytics-ConnectionOrigins-" + viewMode,
            series: series,
        };

        let values = {
            series,
            primaryXAxis: {
                title: "",
                valueType: "Category",
                interval: 1,
                majorGridLines: { width: 0 },
                labelStyle: {
                    fontFamily: "Poppins-Light",
                },
            },
            primaryYAxis: {
                interval: 1,
                majorGridLines: { width: 1 },
                majorTickLines: { width: 0 },
                lineStyle: { width: 0 },
                labelStyle: {
                    fontFamily: "Poppins-Light",
                },
            },
            palettes,
            chartArea: { border: { width: 0 } },
        };

        let specs = getChartSpecs("Pie");
        let tile_theme = getChartTheme(getTheme(), chart, values);

        return (
            <AccumulationChart
                chart={chart}
                values={values}
                specs={specs}
                theme={tile_theme}
            />
        );
    };

    return genTile(stats);
}

function getChartTheme(theme, chart, values) {
    let color = !theme || theme === "light" ? "black" : "white";

    if (!values) return false;

    let ret = Object.assign(values, {
        background: "transparent",
        legendSettings: {
            position: "Right",
            width: "35%",
            textStyle: {
                color: color,
                fontFamily: "Poppins-Regular",
            },
        },
        subTitleStyle: {
            color: color,
        },
        labelStyle: {
            fontFamily: "Poppins-Light",
        },
        tooltip: {
            enable: true,
            textStyle: {
                fontFamily: "Poppins-Light",
            },
        },
    });

    if (ret.primaryXAxis) {
        Object.assign(ret.primaryXAxis, {
            labelStyle: { color: color, fontFamily: "Poppins-Light" },
            titleStyle: { color: color, fontFamily: "Poppins-Light" },
        });
    }

    if (ret.primaryYAxis) {
        Object.assign(ret.primaryYAxis, {
            labelStyle: { color: color, fontFamily: "Poppins-Light" },
            titleStyle: { color: color, fontFamily: "Poppins-Light" },
        });
    }

    delete ret.title;
    return ret;
}